import { FieldErrors } from 'react-hook-form'
import { CheckFormType } from 'src/components/molecules/CheckBoxListWithFilter'
import { validateCheckBoxListValue } from 'src/components/molecules/MemberAssignedStoreCheck'
import { Option } from 'src/components/tables/Cells/SelectCell'
import { RegexEmail } from 'src/utils/regularExpression'
import {
  DISPLAY_OPC_AS_EDITOR_STORE_MANAGER_ORG_CODES,
  ENABLE_ATTACH_EDITOR_STORE_MANAGER_IN_OPERATION_CENTER_ROLE,
} from '../../domain/org'
import { getRoleTitle } from '../../domain/role'
import { RoleEnum } from '../../slices/services/api'
import {
  activeOptions,
  editorSalesReportManagerIndex,
  roleOptions,
} from './type'

export const getRoleOptions = (orgCode: string): Option[] => {
  if (
    !ENABLE_ATTACH_EDITOR_STORE_MANAGER_IN_OPERATION_CENTER_ROLE.includes(
      orgCode
    )
  )
    return roleOptions

  // OPCを「施設担当者」と表示する組織の場合、admin_store_managerとeditor_store_managerを非表示
  if (DISPLAY_OPC_AS_EDITOR_STORE_MANAGER_ORG_CODES.includes(orgCode)) {
    const filteredOptions = roleOptions.filter(
      (option) =>
        option.value !== String(RoleEnum.AdminStoreManager) &&
        option.value !== String(RoleEnum.EditorStoreManager)
    )
    const editorSalesReportManagerIdx = filteredOptions.findIndex(
      (option) => option.value === String(RoleEnum.EditorSalesReportManager)
    )
    if (editorSalesReportManagerIdx !== -1) {
      filteredOptions.splice(editorSalesReportManagerIdx + 1, 0, {
        value: String(RoleEnum.EditorStoreManagerInOperationCenter),
        title: getRoleTitle(
          orgCode,
          RoleEnum.EditorStoreManagerInOperationCenter
        ),
      })
    }
    return filteredOptions
  }

  // 三井不動産など、その他の対象組織の場合
  const newRoleOptions = [...roleOptions]

  const editorStoreManagerInOperationCenter =
    RoleEnum.EditorStoreManagerInOperationCenter
  newRoleOptions.splice(editorSalesReportManagerIndex + 1, 0, {
    value: String(editorStoreManagerInOperationCenter),
    title: getRoleTitle(orgCode, editorStoreManagerInOperationCenter),
  })
  return newRoleOptions
}

export const getRoleOptionsWithAll = (orgCode: string): Option[] => {
  return [{ value: '', title: '全て' }, ...getRoleOptions(orgCode)]
}

type InvalidFormatEmail = {
  index: number
  email: string
}

// メール形式を全件一括検証し、不正な形式のものを index 付きで返す。
// 仮想化でアンマウント中の行は react-hook-form の handleSubmit 検証から外れるため、
// 保存時は formState.errors ではなく値そのものを検証する必要がある
export const findInvalidFormatEmails = (
  members: { email: string }[]
): InvalidFormatEmail[] =>
  members.reduce<InvalidFormatEmail[]>((invalids, { email }, index) => {
    if (!RegexEmail.test(email)) {
      invalids.push({ index, email })
    }
    return invalids
  }, [])

export type ValidatableMember = {
  name?: string
  email?: string
  assignedStores?: CheckFormType
}

export type MembersFormValuesLike = {
  members: (ValidatableMember | undefined)[]
}

export type MembersValidationResult = {
  isValid: boolean
  message?: string
}

// ユーザー新規追加・編集フォームの送信可否をフォームの値から判定する。
// 行削除は unregister で行うため members 配列には穴(undefined)が空く。
// 穴は読み飛ばすが、formErrors は詰めない元の index で参照する必要がある。
// メール形式は formErrors(onBlur 検証結果)のみを見る。値からの一括検証が要る場面では
// 呼び出し側が findInvalidFormatEmails で補う。
export const validateMembersData = <
  TFormValues extends MembersFormValuesLike = MembersFormValuesLike,
>(
  membersData: (ValidatableMember | undefined)[] | undefined,
  formErrors?: FieldErrors<TFormValues>
): MembersValidationResult => {
  const memberErrors = formErrors?.members
  let validMemberCount = 0

  for (let index = 0; index < (membersData?.length ?? 0); index++) {
    const member = membersData?.[index]
    // 削除済みの行は読み飛ばす
    if (member) {
      validMemberCount += 1

      // 氏名が未入力
      if (!member.name || member.name.trim() === '') {
        return { isValid: false }
      }

      // メールアドレスが未入力またはエラーがある
      if (!member.email || member.email.trim() === '') {
        return { isValid: false }
      }
      if (Array.isArray(memberErrors) && memberErrors[index]?.email) {
        return { isValid: false }
      }

      // 担当店舗が未選択
      if (
        !validateCheckBoxListValue(
          member.assignedStores ?? { allChecked: false, checkListValues: [] }
        )
      ) {
        return {
          isValid: false,
          message: '店舗が紐づいていないユーザーがいます',
        }
      }
    }
  }

  if (validMemberCount === 0) {
    return { isValid: false, message: 'ユーザーを追加してください' }
  }

  return { isValid: true }
}

export const activeOptionsWithAll: Option[] = [
  { value: '', title: '全てのステータス' },
  ...activeOptions,
]
